import React from 'react';

import { Checkbox, DatePicker, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { useReceiveStore } from '~/store';
import { formatDate, getDateByOffset } from '~/utils';

import { RECEIVE_MODIFICATION_TYPE } from '~/constants';

import type { TypeInputStatus } from '~/types/common.types';

import styles from './ReceiveRequestDetailDateCell.module.scss';

const DATE_TYPE_OPTIONS = [
  { value: 'expirationDate', text: '유통/소비기한' },
  { value: 'manufactureDate', text: '제조일자' },
];

type TypeReceiveRequestDetailDateCellProps = {
  rowIndex: number;
  isReadOnly: boolean;
};

/**
 * [입고관리 > 입고요청관리] 입고요청상세 모달 > '기한/일자 사용여부' 셀 렌더러
 */
const ReceiveRequestDetailDateCell = ({ rowIndex, isReadOnly }: TypeReceiveRequestDetailDateCellProps) => {
  const [
    detailModalOrderGoodsList,
    setDetailModalOrderGoodsList,
    detailModalOrderGoodsListStatus,
    setDetailModalOrderGoodsListStatus,
  ] = useReceiveStore(
    useShallow((state) => [
      state.detailModalOrderGoodsList,
      state.setDetailModalOrderGoodsList,
      state.detailModalOrderGoodsListStatus,
      state.setDetailModalOrderGoodsListStatus,
    ])
  );

  const rowData = detailModalOrderGoodsList[rowIndex];
  const isModified = rowData?.modifiedFields?.includes('date') ?? false;

  if (!rowData) {
    return <div>-</div>;
  }

  const handleChangeIsDateUsed = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    setDetailModalOrderGoodsList([
      ...detailModalOrderGoodsList.slice(0, rowIndex),
      {
        ...detailModalOrderGoodsList[rowIndex],
        dateUseYn: checked,
        dateType: checked ? DATE_TYPE_OPTIONS[0].value : null,
        date: null,
        modificationType: RECEIVE_MODIFICATION_TYPE.MODIFY,
        modifiedFields: [...(rowData.modifiedFields ?? []), 'date'],
      },
      ...detailModalOrderGoodsList.slice(rowIndex + 1),
    ]);

    if (!checked) {
      resetRowError();
    }
  };

  const handleChangeDateType = (_: unknown, option: TypeSelectOption<string>) => {
    setDetailModalOrderGoodsList([
      ...detailModalOrderGoodsList.slice(0, rowIndex),
      {
        ...detailModalOrderGoodsList[rowIndex],
        dateType: option.value,
        date: null,
        modificationType: RECEIVE_MODIFICATION_TYPE.MODIFY,
        modifiedFields: [...(rowData.modifiedFields ?? []), 'date'],
      },
      ...detailModalOrderGoodsList.slice(rowIndex + 1),
    ]);

    resetRowError();
  };

  const handleChangeDate = (date: Date | null) => {
    const formattedDate = formatDate(date, 'YYYY-MM-DD');

    if (formattedDate === rowData['date']) {
      return;
    }

    setDetailModalOrderGoodsList([
      ...detailModalOrderGoodsList.slice(0, rowIndex),
      {
        ...detailModalOrderGoodsList[rowIndex],
        date: formattedDate,
        modificationType: RECEIVE_MODIFICATION_TYPE.MODIFY,
        modifiedFields: [...(rowData.modifiedFields ?? []), 'date'],
      },
      ...detailModalOrderGoodsList.slice(rowIndex + 1),
    ]);

    resetRowError();
  };

  const resetRowError = () => {
    setDetailModalOrderGoodsListStatus([
      ...detailModalOrderGoodsListStatus.slice(0, rowIndex),
      {
        ...detailModalOrderGoodsListStatus[rowIndex],
        date: 'default' as TypeInputStatus,
      },
      ...detailModalOrderGoodsListStatus.slice(rowIndex + 1),
    ]);
  };

  return (
    <div className={styles['date-cell-container']}>
      <Checkbox
        checked={rowData['dateUseYn']}
        disabled={isReadOnly}
        size="medium"
        label="사용"
        onChange={handleChangeIsDateUsed}
      />
      <div className={styles['date-cell-inputs']}>
        <Select
          className={styles['date-cell-input']}
          size="tiny"
          maxWidth="112px"
          placeholder="종류 선택"
          dropdownPortalTarget={document.body}
          shouldCloseOnNotInView
          disabled={isReadOnly || !rowData['dateUseYn']}
          value={rowData['dateType'] ? DATE_TYPE_OPTIONS.find((option) => option.value === rowData['dateType']) : null}
          options={DATE_TYPE_OPTIONS}
          onChange={handleChangeDateType}
        />
        <DatePicker
          className={`${styles['date-cell-input']} ${isModified ? styles['modified'] : ''}`}
          size="tiny"
          placeholder="날짜 선택"
          portalTarget={document.body}
          shouldCloseOnNotInView
          disabled={isReadOnly || !rowData['dateUseYn']}
          minDate={rowData['dateType'] === 'expirationDate' ? getDateByOffset(0, 'day') : undefined}
          status={detailModalOrderGoodsListStatus[rowIndex]?.['date'] ?? 'default'}
          value={rowData['date'] ? new Date(rowData['date']) : null}
          onChange={handleChangeDate}
        />
      </div>
    </div>
  );
};

export default ReceiveRequestDetailDateCell;
